// @flow
import React, { type Node, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import values from 'ramda/es/values';
import find from 'ramda/es/find';
import pipe from 'ramda/es/pipe';
import path from 'ramda/es/path';
import sortBy from 'ramda/es/sortBy';
import propEq from 'ramda/es/propEq';
import prop from 'ramda/es/prop';

import Modal, { ModalHeader, ModalContent } from 'app/common/components/Modal';
import { useSettingsContext } from 'app/common/components-next/settings-data';
import PlainButton from 'app/common/ui-next/plain-button';
import Loading, { DotsLoader } from 'app/common/components/Loader';

import useSellPointsData from './use-sell-points-data';
import type { SellPoint } from './sell-points-data-types';
import SellPointsContext from './sell-points-context';
import styles from './sell-points-data.module.scss';;


type Props = {
  children: Node | (currentSellPointId: ?number) => Node,
  personId: number,
  title: string,
  onSellPoint?: (sp: SellPoint | null) => void,
  schoolId?: number,
}

const SellPoints = ({ children, personId, onSellPoint, title, schoolId }: Props) => {
  const [selectorActive, viewSelector] = useState(false);
  const [savingId, setSavingId] = useState<number>(0);

  const toggleSelector = useCallback(() => {
    viewSelector(a => !a);
  }, []);


  // sellPoints
  const [sellPoints, sellPointsLoading] = useSellPointsData(personId, schoolId);
  const sellPointsAvailable: Array<SellPoint> = useMemo(() => pipe(
    values,
    sortBy(prop('building_id')),
  )(sellPoints), [sellPoints]);


  // settings
  const [[settings, loadingSettings], settingsActions] = useSettingsContext();
  const [savingSettings, setSavingSettings] = useState(false);

  // request settings
  useEffect(() => {
    settingsActions.requestSettings();
  }, []);

  // loading
  const previosPersonId = useRef(null);
  const previosSchoolId = useRef(null);

  const loading = useMemo(() => {
    const prevPersonId = previosPersonId.current;
    const prevSchoolId = previosSchoolId.current;
    previosPersonId.current = personId;
    previosSchoolId.current = schoolId;
    // предотвращаем цикл, когда personId сменился а loading еще не true
    if (prevPersonId !== personId || prevSchoolId !== schoolId) {
      return true;
    }
    return sellPointsLoading || loadingSettings;
  }, [sellPointsLoading, loadingSettings, personId, schoolId]);


  const currentSellPointId: number = path(['common', 'currentSellPointByPersonIdAndSchoolId', `${personId}_${schoolId}`], settings);
  const currentSellPoint: SellPoint | null = find(propEq('id', currentSellPointId), sellPointsAvailable) || null;
  const isSellPointIdValid = Boolean(!loading && currentSellPointId && currentSellPoint);


  useEffect(() => {
    if (!loading) {
      if (
        !currentSellPointId // не установлено текущее значение
        || !isSellPointIdValid // текущее значение невалидное
      ) {
        if (sellPointsAvailable.length > 1) {
          // даем мользователю выбрать
          viewSelector(true);
        } else if (sellPointsAvailable.length === 1) {
          // если точка одна, выбираем ее автоматически
          (async () => {
            setSavingSettings(true);
            await settingsActions.saveSettingsKeyValue('common', 'currentSellPointByPersonIdAndSchoolId', {
              [`${personId}_${schoolId}`]: sellPointsAvailable[0].id,
            });
            setSavingSettings(false);
          })();
        }
        return;
      }
    }
    viewSelector(false);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    currentSellPointId,
    sellPointsAvailable,
    personId,
    currentSellPoint,
    loading,
    isSellPointIdValid,
  ]);
  /* eslint-enable */


  const handleSelectSellPoint = useCallback(async (e: SyntheticEvent<HTMLButtonElement>) => {
    const { id } = e.currentTarget;
    const spId = parseInt(id, 10);
    setSavingId(spId);
    if (spId) {
      setSavingSettings(true);
      await settingsActions.saveSettingsKeyValue('common', 'currentSellPointByPersonIdAndSchoolId', {
        [`${personId}_${schoolId}`]: spId,
      });
      setSavingSettings(false);
    }
  }, [personId, settingsActions, schoolId]);

  const handleShowSelector = useCallback(() => {
    viewSelector(true);
  }, []);


  useEffect(() => {
    if (!onSellPoint) return;
    onSellPoint(isSellPointIdValid ? currentSellPoint : null);
  }, [currentSellPoint, onSellPoint, isSellPointIdValid]);


  const contextValue = useMemo(() => ({
    loading,
    currentSellPoint,
    handleShowSelector,
  }), [
    loading,
    currentSellPoint,
    handleShowSelector,
  ]);

  if (loadingSettings) {
    return <Loading />;
  }


  return (
    <SellPointsContext.Provider value={contextValue}>
      {typeof children === 'function'
        ? children(isSellPointIdValid ? currentSellPointId : null)
        : children}
      {selectorActive
        && <Modal
          // если никакая точка не выбрана, то нельзя закрывать окно крестиком
          toggleModal={currentSellPoint ? toggleSelector : undefined}
        >
          <ModalHeader className={styles.header}>{`${title}:`}</ModalHeader>
          <ModalContent className={styles.content}>
            {sellPointsAvailable.map(({ id, title: tt, building_title: bTitle }: SellPoint) => (
              <PlainButton
                key={id}
                id={String(id)}
                className={styles.button}
                onClick={handleSelectSellPoint}
                disabled={savingSettings || id === currentSellPointId}
                outline
              >
                {savingSettings && savingId === id
                  ? <span>
                    &nbsp;
                    <DotsLoader simple />
                    &nbsp;
                  </span>
                  : `${tt} — ${bTitle}`}
              </PlainButton>
            ))}
          </ModalContent>
        </Modal>}
    </SellPointsContext.Provider>
  );
};

export default SellPoints;
